import React, { useState } from 'react';
import FontAwesomeIcon from './FontAwesomeIcon';

export default function NewsletterForm({ inline = false }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'newsletter',
          'bot-field': '',
          email,
        }).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setEmail('');
        setTimeout(() => setSubmitted(false), 5000); // Reset after 5 seconds
      } else {
        throw new Error('Subscription failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Newsletter subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (inline) {
    // Inline version for footer or banners
    return (
      <form
        name="newsletter"
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
        className="w-full max-w-md"
      >
        <input type="hidden" name="form-name" value="newsletter" />
        <input type="hidden" name="bot-field" />

        {submitted ? (
          <div className="bg-green-500 text-white px-4 py-3 rounded-lg flex items-center gap-2">
            <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5" />
            <span>Successfully subscribed! Check your email.</span>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="email"
              name="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
              placeholder="Your email address"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-safari-gold text-safari-green px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <FontAwesomeIcon icon="fa-spinner" className="w-5 h-5 animate-spin" />
              ) : (
                'Subscribe'
              )}
            </button>
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
            <FontAwesomeIcon icon="fa-exclamation-circle" className="w-4 h-4" />
            {error}
          </p>
        )}
      </form>
    );
  }

  // Full version for dedicated newsletter sections
  return (
    <div className="bg-gradient-to-r from-safari-green to-safari-green/90 text-white rounded-xl p-8 md:p-12">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 bg-safari-gold rounded-full flex items-center justify-center mx-auto mb-6">
          <FontAwesomeIcon icon="fa-envelope" className="w-8 h-8 text-safari-green" />
        </div>

        <h3 className="text-3xl font-bold mb-4 font-heading">
          Get Safari Inspiration in Your Inbox
        </h3>
        <p className="text-gray-100 text-lg mb-8">
          Subscribe to receive exclusive travel tips, safari guides, special offers, and
          inspiring stories from Uganda's wild heart.
        </p>

        <form
          name="newsletter"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form-name" value="newsletter" />
          <input type="hidden" name="bot-field" />

          {submitted ? (
            <div className="bg-white text-safari-green px-6 py-4 rounded-lg inline-flex items-center gap-3">
              <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6" />
              <div className="text-left">
                <p className="font-semibold">Welcome to the tribe!</p>
                <p className="text-sm">Check your email to confirm your subscription.</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="flex-1 px-6 py-4 text-gray-900 border-2 border-transparent rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
                placeholder="Enter your email address"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-safari-gold text-safari-green px-8 py-4 rounded-lg font-bold hover:bg-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon
                      icon="fa-spinner"
                      className="w-5 h-5 mr-2 inline animate-spin"
                    />
                    Subscribing...
                  </>
                ) : (
                  <>
                    Subscribe
                    <FontAwesomeIcon icon="fa-arrow-right" className="w-5 h-5 ml-2 inline" />
                  </>
                )}
              </button>
            </div>
          )}

          {error && (
            <p className="text-red-300 mt-4 flex items-center justify-center gap-2">
              <FontAwesomeIcon icon="fa-exclamation-circle" className="w-5 h-5" />
              {error}
            </p>
          )}
        </form>

        <p className="text-gray-300 text-sm mt-6">
          We respect your privacy. Unsubscribe anytime. No spam, ever.
        </p>
      </div>
    </div>
  );
}
